// src/pages/onboarding/Step4PentestSimulation.jsx
import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Chip,
  Stack,
  Tooltip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ApiOutlinedIcon from '@mui/icons-material/ApiOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import DnsOutlinedIcon from '@mui/icons-material/DnsOutlined';

const pentestCards = [
  {
    title: 'External Pentest',
    description:
      'External attack surface discovery and exploitation against internet-facing assets.',
    available: true,
    image:
      'https://cdn.midjourney.com/87668ff4-21a0-4c9a-8331-897d3e21d1b0/0_1.png',
    video:
      'https://cdn.midjourney.com/video/41807f4c-922b-4a37-9e16-b52d790efe07/3.mp4',
    tags: ['Popular', 'Network'],
    compliance: ['SOC 2', 'PCI', 'HIPAA'],
    icon: <LanguageOutlinedIcon fontSize="small" />,
    details: {
      description:
        'Simulates a real attacker on the public internet targeting your domains, IPs, and cloud edge. Our AI Agents map, probe, and exploit where possible to show true business risk.',
      scope: ['Domains', 'IPs', 'CIDRs'],
      methodology: [
        'Reconnaissance: Subdomain enumeration, port scanning, service fingerprinting',
        'Service checks: HTTP/HTTPS, SSH, SMTP, VPN, etc.',
        'Vulnerability checks: CVEs, misconfigurations, known exploits',
        'Reporting: Findings, risk ratings, actionable fixes',
      ],
      resources: [{ label: 'Sample Report (External)', url: '#' }],
    },
    id: 'external',
    heroType: 'pdf',
  },
  {
    title: 'Vishing Pentest',
    description: 'Voice phishing simulations to test employee resilience.',
    available: true,
    image:
      'https://cdn.midjourney.com/f8a1314a-bdd6-4475-8b8b-7b64297ab91e/0_2.png',
    video:
      'https://cdn.midjourney.com/video/c44d317b-3f9c-4153-b24b-bac1b0b7f086/1.mp4',
    tags: ['Social Engineering', 'New'],
    compliance: ['SOC 2'],
    icon: <CallOutlinedIcon fontSize="small" />,
    details: {
      description:
        'AI voice agents run approved social-engineering scenarios to measure and improve human risk without disrupting your team.',
      scope: ['Phone Numbers', 'Scenario', 'Voice'],
      methodology: [
        'Scripted call from AI Agent with chosen voice',
        'Influence techniques: urgency, authority, familiarity',
        'Measure credential/MFA handling & response',
        'Reporting: training recommendations & metrics',
      ],
      resources: [
        { label: 'Sample Call', url: '#' },
        { label: 'Sample Report (Vishing)', url: '#' },
      ],
    },
    id: 'vishing',
    heroType: 'video',
    heroUrl: 'https://player.vimeo.com/video/1064351192',
  },
  {
    title: 'Dark Web Monitoring',
    description:
      'Continuous monitoring for leaked creds, PII, and brand mentions.',
    available: true,
    image:
      'https://cdn.midjourney.com/65e39ab0-7b22-4a0b-9587-af8499360a24/0_3.png',
    video:
      'https://cdn.midjourney.com/video/d6d3e012-9b8f-4075-8744-d35a28c639f4/0.mp4',
    tags: ['Threat Intel', 'Popular'],
    compliance: ['SOC 2', 'HIPAA'],
    icon: <ShieldOutlinedIcon fontSize="small" />,
    details: {
      description:
        'Proactive monitoring across breach dumps, paste sites, and dark-web forums to catch exposure before attackers weaponize it.',
      scope: ['Domain'],
      methodology: [
        'Collection: breach dumps, paste sites, forums, marketplaces',
        'Detection: org emails, leaked creds, sensitive mentions',
        'Reporting: snapshots, risk ratings, remediation guidance',
      ],
      resources: [{ label: 'Sample Findings Snapshot', url: '#' }],
    },
    id: 'darkweb',
    heroType: 'image',
  },
  {
    title: 'API Pentest',
    description:
      'Specification-driven and exploratory testing of APIs.',
    available: true,
    image:
      'https://cdn.midjourney.com/b4913fb2-527d-4f94-9335-089b617ebf6f/0_0.png',
    video:
      'https://cdn.midjourney.com/video/6cfdd050-611a-451c-8baa-64f2532221e7/1.mp4',
    tags: ['API', 'New'],
    compliance: ['SOC 2', 'PCI', 'HIPAA'],
    icon: <ApiOutlinedIcon fontSize="small" />,
    details: {
      description:
        'Deep testing against API endpoints for auth, authz, data exposure, and business-logic abuse. Ideal if your product is API-first.',
      scope: ['Domain', 'Credentials', 'API Docs (OpenAPI/Postman)'],
      methodology: [
        'Docs review & endpoint mapping',
        'User journey recon & role analysis',
        'Threat modeling: auth, authz, logic abuse, data safety',
        'Active testing & fuzzing (OWASP API Top 10)',
        'Reporting with evidence & fixes',
      ],
      resources: [{ label: 'Sample Report (API)', url: '#' }],
    },
    id: 'api',
  },
  {
    title: 'Web Application Pentest',
    description:
      'End-to-end app testing (OWASP Top 10, logic, auth). Coming Soon!',
    available: false,
    image:
      'https://cdn.midjourney.com/65e39ab0-7b22-4a0b-9587-af8499360a24/0_1.png',
    video:
      'https://cdn.midjourney.com/video/db9f19d0-5f18-4107-a7e5-28d1519047d4/1.mp4',
    tags: ['Popular', 'AppSec'],
    compliance: ['SOC 2', 'PCI', 'HIPAA'],
    icon: <BugReportOutlinedIcon fontSize="small" />,
    details: {
      description:
        'Simulates real attacks against your web app to uncover vulnerabilities and logic flaws before adversaries do.',
      scope: ['TBD'],
      methodology: ['TBD'],
      resources: [{ label: 'Coming Soon', url: '#' }],
    },
    id: 'webapp',
  },
  {
    title: 'Internal Pentest',
    description: 'On-network adversary simulation. Coming Soon!',
    available: false,
    image:
      'https://cdn.midjourney.com/6f27c8ca-6356-467a-a2cc-39eab5412810/0_1.png',
    video:
      'https://cdn.midjourney.com/video/45f0ed37-deb7-4a4f-9e8e-428e084b5ad0/1.mp4',
    tags: ['Network', 'Enterprise'],
    compliance: ['SOC 2', 'HIPAA'],
    icon: <DnsOutlinedIcon fontSize="small" />,
    details: {
      description:
        'Assesses risk inside your corporate network (AD, lateral movement, privilege escalation) to model insider or post-phish compromise.',
      scope: ['TBD'],
      methodology: ['TBD'],
      resources: [{ label: 'Coming Soon', url: '#' }],
    },
    id: 'internal',
  },
];

export default function PentestSimulation() {
  const [hoveredId, setHoveredId] = useState(null);
  const [openCard, setOpenCard] = useState(null);

  const handleOpen = (card) => setOpenCard(card);
  const handleClose = () => setOpenCard(null);

  const renderMedia = (card) => {
    const isHovered = hoveredId === card.id;
    const canVideo = Boolean(card.video);

    // Only this card switches to video on hover
    if (isHovered && canVideo) {
      return (
        <CardMedia
          component="video"
          height="200"
          src={card.video}
          autoPlay
          muted
          loop
          playsInline
          sx={{ objectFit: 'cover' }}
        />
      );
    }
    return (
      <CardMedia
        component="img"
        height="200"
        image={card.image}
        alt={card.title}
        sx={{ objectFit: 'cover' }}
      />
    );
  };

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Grid container spacing={2}>
        {pentestCards.map((card) => (
          <Grid key={card.id} item xs={12} sm={6} md={4}>
            <Card
              onMouseEnter={() => setHoveredId(card.id)}
              onMouseLeave={() => setHoveredId((id) => (id === card.id ? null : id))}
              sx={{
                height: '100%',
                borderRadius: 3,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                transition: 'transform 220ms ease, box-shadow 220ms ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 36px rgba(0,0,0,0.12)',
                },
                position: 'relative',
                opacity: card.available ? 1 : 0.8,
                filter: card.available ? 'none' : 'grayscale(0.35)',
                bgcolor: 'background.paper',
              }}
              elevation={0}
            >
              {/* media (image → video on hover) */}
              {renderMedia(card)}

              {/* badges over media */}
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                }}
              >
                {card.tags?.slice(0, 2).map((t) => (
                  <Chip
                    key={t}
                    label={t}
                    size="small"
                    color="default"
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.9)',
                      '& .MuiChip-label': { fontWeight: 700 },
                      borderRadius: 1.5,
                    }}
                  />
                ))}
                {!card.available && (
                  <Chip
                    label="Coming soon"
                    size="small"
                    color="warning"
                    sx={{ borderRadius: 1.5, fontWeight: 700 }}
                  />
                )}
              </Stack>

              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                  {card.icon}
                  <Typography variant="h6" fontWeight={800}>
                    {card.title}
                  </Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  {card.description}
                </Typography>

                {/* compliance tags */}
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                  {(card.compliance || []).map((c) => (
                    <Chip
                      key={c}
                      label={c}
                      size="small"
                      variant="outlined"
                      sx={{ borderRadius: 1.5 }}
                    />
                  ))}
                </Stack>

                <Stack direction="row" spacing={1} mt="auto">
                  {card.available ? (
                    <>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleOpen(card)}
                        startIcon={<InfoOutlinedIcon />}
                        sx={{ borderRadius: 2, fontWeight: 700 }}
                      >
                        Details
                      </Button>
                      <Tooltip title="Preview media">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setHoveredId(card.id)}
                          startIcon={<PlayCircleOutlineIcon />}
                          sx={{ borderRadius: 2, fontWeight: 700 }}
                        >
                          Preview
                        </Button>
                      </Tooltip>
                    </>
                  ) : (
                    <Tooltip title="This agent is coming soon">
                      <span>
                        <Button
                          variant="outlined"
                          size="small"
                          disabled
                          sx={{ borderRadius: 2, fontWeight: 700 }}
                        >
                          Not available
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Details dialog */}
      <Dialog
        open={Boolean(openCard)}
        onClose={handleClose}
        PaperProps={{ sx: { width: { xs: '100%', md: '85%' }, height: { xs: '90%', md: '80%' }, borderRadius: 3 } }}
        maxWidth={false}
      >
        {openCard && (
          <>
            <DialogTitle sx={{ m: 0, p: 2, pr: 6 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                {openCard.icon}
                <Typography variant="h6" fontWeight={900}>
                  {openCard.title}
                </Typography>
                {!openCard.available && (
                  <Chip label="Coming soon" size="small" color="warning" sx={{ ml: 1 }} />
                )}
              </Stack>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{ position: 'absolute', right: 12, top: 12 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ overflowY: 'auto' }}>
              <Grid container spacing={4}>
                {/* LEFT: Overview */}
                <Grid item xs={12} md={6}>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <InfoOutlinedIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={800}>
                        Overview
                      </Typography>
                    </Stack>
                    <Typography variant="body1" color="text.secondary">
                      {openCard.details?.description}
                    </Typography>

                    <Divider />

                    <Stack spacing={1}>
                      {openCard.details?.scope?.length ? (
                        <>
                          <Typography variant="subtitle2" fontWeight={800}>
                            Scope
                          </Typography>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {openCard.details.scope.map((s) => (
                              <Chip key={s} label={s} size="small" />
                            ))}
                          </Stack>
                        </>
                      ) : null}
                    </Stack>

                    {openCard.details?.methodology?.length ? (
                      <>
                        <Divider />
                        <Typography variant="subtitle2" fontWeight={800}>
                          Methodology
                        </Typography>
                        <Stack spacing={0.8}>
                          {openCard.details.methodology.map((m) => (
                            <Typography key={m} variant="body2" color="text.secondary">
                              • {m}
                            </Typography>
                          ))}
                        </Stack>
                      </>
                    ) : null}

                    {openCard.details?.resources?.length ? (
                      <>
                        <Divider />
                        <Typography variant="subtitle2" fontWeight={800}>
                          Resources
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {openCard.details.resources.map((r) => (
                            <Chip
                              key={r.label}
                              label={r.label}
                              component="a"
                              href={r.url}
                              clickable
                              variant="outlined"
                            />
                          ))}
                        </Stack>
                      </>
                    ) : null}
                  </Stack>
                </Grid>

                {/* RIGHT: Media demo area */}
                <Grid item xs={12} md={6}>
                  {openCard.id === 'external' && (
                    <>
                      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <ArticleOutlinedIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight={800}>
                          Sample Pentest Report
                        </Typography>
                      </Stack>
                      <Box sx={{ width: '100%', height: 560, borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.50' }}>
                        {/* Replace with a real PDF when ready */}
                        <iframe
                          src="about:blank"
                          title="Sample Report"
                          width="100%"
                          height="100%"
                          style={{ border: 0 }}
                        />
                      </Box>
                    </>
                  )}

                  {openCard.id === 'vishing' && (
                    <>
                      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <PlayCircleOutlineIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight={800}>
                          Vishing Demo
                        </Typography>
                      </Stack>
                      <Box sx={{ width: '100%', height: 560, borderRadius: 2, overflow: 'hidden', bgcolor: 'grey.50' }}>
                        <iframe
                          src={openCard.heroUrl}
                          style={{ width: '100%', height: '100%', border: 0 }}
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title="Vishing Demo"
                        />
                      </Box>
                    </>
                  )}

                  {openCard.id === 'darkweb' && (
                    <>
                      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                        <ImageOutlinedIcon color="primary" />
                        <Typography variant="subtitle1" fontWeight={800}>
                          Dark-Web Findings Snapshot
                        </Typography>
                      </Stack>
                      <Box sx={{ width: '100%', height: 560, borderRadius: 2, overflow: 'hidden' }}>
                        <CardMedia
                          component="img"
                          image={openCard.image}
                          alt="Dark-Web Findings"
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    </>
                  )}

                  {/* Fallback media for other cards */}
                  {['api', 'webapp', 'internal'].includes(openCard.id) && (
                    <Box sx={{ width: '100%', height: 560, borderRadius: 2, overflow: 'hidden' }}>
                      <CardMedia
                        component="video"
                        src={openCard.video}
                        autoPlay
                        muted
                        loop
                        playsInline
                        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: openCard.video ? 'block' : 'none' }}
                      />
                      {!openCard.video && (
                        <CardMedia
                          component="img"
                          image={openCard.image}
                          alt={openCard.title}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
}
